import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Logo from "../../common/Logo";
import CustomButton from "../../components/Common/CustomButton";
import { CargoVideoUrl, GOOGLE_AUTH_URL } from "../../constants";
import googleLogo from "../../img/google-logo.png";
import { saveObject, toastError } from "../../utils";
import loginValidation from "../../validations/loginValidation";
import * as actions from "./actions";
import AuthInput from "./AuthInput";

import ForgotPasswordView from "./ForgotPassword";
import SignUpView from "./SignUp/index";

const fadeTransition = { duration: 0.25, ease: "easeOut" };

function Auth() {
  const history = useHistory();
  const location = useLocation();

  const [view, setView] = useState("login");

  const [sharedEmail, setSharedEmail] = useState("");
  const [sharedPassword, setSharedPassword] = useState("");

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const emailid = queryParams.get("mailid");
    const password = queryParams.get("pas");
    if (emailid && password) {
      const isLoginBySuperAdmin = queryParams.get("isLoginBySuperAdmin");
      actions
        .login({ email: emailid, password: password, source: "google" })
        .then((res) => {
          saveObject("auth", {
            ...res.data,
            isLoginBySuperAdmin: Boolean(isLoginBySuperAdmin),
          });
          history.push("/welcome");
        })
        .catch((err) => {
          toastError(err?.data?.message);
        });
    }
  }, []);

  return (
    <div className="relative w-full h-screen overflow-hidden flex items-center justify-center md:justify-end bg-slate-900 md:pr-28">
      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        <video autoPlay loop muted className="w-full h-full object-cover">
          <source src={CargoVideoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        {/* Sleek right-to-left gradient overlay */}
        {/* <div className="absolute inset-0 bg-gradient-to-l from-[#0a1a36]/90 via-[#0a1a36]/40 to-transparent backdrop-blur-[1px]" /> */}
      </div>

      {/* Main SaaS Card Container with layout transition and spacious width/padding */}
      <motion.div
        layout
        transition={{ type: "spring", stiffness: 380, damping: 35 }}
        className="relative z-10 w-full max-w-[440px] mx-4 bg-white rounded-[24px] p-6 sm:p-8 border border-slate-100/80 shadow-[0_20px_50px_rgba(16,63,107,0.08)] flex flex-col gap-4"
      >
        <AnimatePresence mode="wait">
          {view === "login" && (
            <motion.div
              key="login"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={fadeTransition}
            >
              <SignInView
                setView={setView}
                email={sharedEmail}
                setEmail={setSharedEmail}
                password={sharedPassword}
                setPassword={setSharedPassword}
              />
            </motion.div>
          )}
          {view === "signup" && (
            <motion.div
              key="signup"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={fadeTransition}
            >
              <SignUpView
                setView={setView}
                initialEmail={sharedEmail}
                setEmail={setSharedEmail}
                initialPassword={sharedPassword}
                setPassword={setSharedPassword}
              />
            </motion.div>
          )}
          {view === "forgot" && (
            <motion.div
              key="forgot"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={fadeTransition}
            >
              <ForgotPasswordView
                setView={setView}
                initialEmail={sharedEmail}
                setEmail={setSharedEmail}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

function SignInView({ setView, email, setEmail, password, setPassword }) {
  const history = useHistory();
  const location = useLocation();
  const [loginLoader, setLoginLoader] = useState(false);
  const [errors, setErrors] = useState({});

  const onChangeHandler = (e) => {
    const { name = "", value = "" } = e.target;
    if (name === "email") setEmail(value);
    if (name === "password") setPassword(value);
    setErrors({ ...errors, [name]: "" });
  };

  const keyPressed = (e) => {
    if (e.key === "Enter") {
      if (!loginLoader) {
        onLogin();
      }
    }
  };

  const onLogin = () => {
    const queryParams = new URLSearchParams(location.search);
    const isLoginBySuperAdmin = queryParams.get("isLoginBySuperAdmin");

    const { isValid, errors } = loginValidation({ email, password });
    if (!isValid) return setErrors(errors);
    setLoginLoader(true);
    actions
      .login({ email, password })
      .then((res) => {
        setLoginLoader(false);
        saveObject("auth", {
          ...res.data,
          isLoginBySuperAdmin: Boolean(isLoginBySuperAdmin),
        });
        history.push("/welcome");
      })
      .catch((err) => {
        setLoginLoader(false);
        toastError(err?.data?.message);
      });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Central Brand Badge Header */}
      <div className="flex flex-col items-center gap-2">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center">
          <Logo className="w-8 h-8" />
        </div>
        <div className="text-center">
          <h3 className="text-xl font-bold text-[#0a2540] tracking-tight">Smart Sea Freight</h3>
          <p className="text-xs font-medium text-slate-400 mt-1">Access your logistics dashboard</p>
        </div>
      </div>

      {/* Input Form Fields */}
      <div className="flex flex-col gap-4">
        <AuthInput
          name="email"
          type="email"
          placeholder="name@company.com"
          label="Email Address"
          value={email}
          error={!!errors.email}
          errorMsg={errors.email}
          onChange={onChangeHandler}
        />
        <AuthInput
          name="password"
          type="password"
          placeholder="••••••••"
          label="Password"
          value={password}
          error={!!errors.password}
          errorMsg={errors.password}
          onChange={onChangeHandler}
          onKeyPress={keyPressed}
          rightLabelElement={
            <button
              type="button"
              onClick={() => setView("forgot")}
              className="text-xs font-semibold text-[#313C4C] hover:text-sky-800 transition-colors duration-200 focus:outline-none cursor-pointer"
            >
              Forgot Password?
            </button>
          }
        />
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-4 mt-2">
        <CustomButton
          onClick={onLogin}
          loader={loginLoader}
          disabled={loginLoader}
          className="bg-[#0a2540] hover:bg-[#001c3d] text-white py-3.5 rounded-2xl font-semibold tracking-wide shadow-lg shadow-[#0a2540]/10 hover:shadow-[#0a2540]/20 transition-all duration-300 w-full text-sm border-none cursor-pointer"
        >
          Sign In
        </CustomButton>

        {/* Divider */}
        <div className="flex items-center gap-4 my-1">
          <div className="h-[1px] flex-1 bg-[#e2e8f0]" />
          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
            or continue with
          </span>
          <div className="h-[1px] flex-1 bg-[#e2e8f0]" />
        </div>

        {/* Google Login Button */}
        <a
          href={GOOGLE_AUTH_URL}
          className="flex items-center justify-center gap-3 w-full border border-[#e1e9f4] hover:border-slate-300 hover:bg-slate-50 bg-white text-slate-700 py-3 rounded-2xl font-semibold text-sm transition-all duration-200 shadow-sm cursor-pointer no-underline"
        >
          <img
            src={googleLogo}
            alt="Google"
            className="w-4 h-4 object-contain"
          />
          <span>Continue with Google</span>
        </a>
      </div>

      {/* Footer Link */}
      <div className="text-center pt-2">
        <p className="text-sm text-slate-500">
          Don't have an account?{" "}
          <button
            type="button"
            onClick={() => setView("signup")}
            className="text-[#313C4C] font-bold hover:text-sky-800 transition-colors duration-200 focus:outline-none cursor-pointer"
          >
            Create Account
          </button>
        </p>
      </div>
    </div>
  );
}

export default Auth;
